import React, { useContext, useState, useEffect } from "react";
import { TranslatorContext } from "../../context/Translator";
import { Row, Col, Button, Modal, Form, Alert, Spinner, Accordion, Badge } from "react-bootstrap";
import axios from "axios";
import { FaEdit, FaTrash, FaPlus, FaStethoscope, FaFileAlt, FaCalendarAlt, FaBell, FaCut, FaCheckCircle, FaMoneyBillWave } from "react-icons/fa";
import moment from "moment";
import { MdDateRange, MdAccessTime } from "react-icons/md";
import Select from "react-select";
import Swal from 'sweetalert2';

export default function PetGroomingComponent({ appointmentId, doctorId, petId, isAppointmentOpen }) {
    const { t } = useContext(TranslatorContext);
    const [petGroomings, setPetGroomings] = useState([]);
    const [groomings, setGroomings] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showModal, setShowModal] = useState(false);
    const [editingGrooming, setEditingGrooming] = useState(null);
    const [selectedGroomings, setSelectedGroomings] = useState([]);
    const [selectedGroomingRows, setSelectedGroomingRows] = useState([]);
    const [recomendation, setRecomendation] = useState("");
    const [groomingDate, setGroomingDate] = useState("");
    const [generalNote, setGeneralNote] = useState("");
    const [existingNoteId, setExistingNoteId] = useState(null);
    const [openAccordion, setOpenAccordion] = useState(null);
    const [submitting, setSubmitting] = useState(false);
    const [isRecommended, setIsRecommended] = useState(false);

    const [completeModal, setCompleteModal] = useState(false);
    const [selectedCompleteGrooming, setSelectedCompleteGrooming] = useState(null);
    const [completePrice, setCompletePrice] = useState("");
    const [completeReminderDate, setCompleteReminderDate] = useState("");
    const [completeStatus, setCompleteStatus] = useState("pending");
    const [hasGroomingForAppointment, setHasGroomingForAppointment] = useState(false);

    const userRole = localStorage.getItem("role");
    const permissions = JSON.parse(localStorage.getItem("permissions")) || [];

    const hasPermission = (perm) => {
        return permissions.includes(perm);
    };

    const canDelete = () => {
        return userRole === 'Admin';
    };

    const showSuccessToast = (message) => {
        Swal.fire({
            icon: 'success',
            title: t('success'),
            text: message,
            toast: true,
            position: 'top-end',
            showConfirmButton: false,
            timer: 3000,
            timerProgressBar: true,
        });
    };

    const showErrorToast = (message) => {
        Swal.fire({
            icon: 'error',
            title: t('error'),
            text: message,
            toast: true,
            position: 'top-end',
            showConfirmButton: false,
            timer: 3000,
            timerProgressBar: true,
        });
    };

    const showWarningToast = (message) => {
        Swal.fire({
            icon: 'warning',
            title: t('warning'),
            text: message,
            toast: true,
            position: 'top-end',
            showConfirmButton: false,
            timer: 3000,
            timerProgressBar: true,
        });
    };

    const getReminderStatusBadge = (status) => {
        switch (status) {
            case 'completed':
                return 'success';
            case 'pending':
                return 'warning';
            case 'missed':
                return 'danger';
            case 'cancelled':
                return 'secondary';
            default:
                return 'warning';
        }
    };

    useEffect(() => {
        fetchPetGroomings();
    }, [appointmentId, petId]);

    const fetchPetGroomings = async () => {
        try {
            setLoading(true);
            const AuthToken = localStorage.getItem("authToken");
            const response = await axios.get(
                `${process.env.REACT_APP_API_URL}/pet-grooming/${petId}`,
                { headers: { Authorization: `Bearer ${AuthToken}` } }
            );

            if (response.data) {
                const groomingsForPet = response.data.pet_grooming || [];
                setPetGroomings(groomingsForPet);
                setGroomings(response.data.groomings || []);
                const hasGrooming = groomingsForPet.some(grooming => String(grooming?.appointment_id) === String(appointmentId));
                setHasGroomingForAppointment(hasGrooming);
                setOpenAccordion(String(appointmentId));
            }
        } catch (error) {
            console.error("Error fetching pet groomings:", error);
            showErrorToast(t('failed to load groomings'));
            setPetGroomings([]);
            setGroomings([]);
        } finally {
            setLoading(false);
        }
    };

    const findChildGroomingById = (id) => {
        for (const parent of groomings) {
            if (parent.child_groomings) {
                const child = parent.child_groomings.find(c => c.id === id);
                if (child) return child;
            }
        }
        return null;
    };

    const handleGroomingRowChange = (rowKey, field, value) => {
        setSelectedGroomingRows(prevRows => prevRows.map(row => row.rowKey === rowKey ? {
            ...row,
            [field]: value,
        } : row));
    };

    const removeSelectedGroomingRow = (rowKey) => {
        setSelectedGroomingRows(prev => {
            const next = prev.filter(row => row.rowKey !== rowKey);
            setSelectedGroomings(next.map(row => row.grooming_id));
            return next;
        });
    };

    const handleGroomingSelect = (selected) => {
        const groomingIds = Array.isArray(selected) ? selected.map(item => item.value) : [];
        setSelectedGroomings(groomingIds);

        setSelectedGroomingRows(prevRows => {
            return groomingIds.map(id => {
                const existing = prevRows.find(row => row.grooming_id === id);
                const child = findChildGroomingById(id);
                return {
                    rowKey: existing ? existing.rowKey : `new-${id}-${Date.now()}`,
                    id: existing ? existing.id : undefined,
                    grooming_id: id,
                    grooming_name: child?.name || existing?.grooming_name || '',
                    price: existing ? existing.price : (child?.price ? child.price.toString() : ""),
                    reminder_date: existing ? existing.reminder_date : '',
                };
            });
        });
    };

    const resetModalState = () => {
        setEditingGrooming(null);
        setSelectedGroomings([]);
        setSelectedGroomingRows([]);
        setRecomendation("");
        setGroomingDate("");
        setGeneralNote("");
        setExistingNoteId(null);
        setIsRecommended(false);
    };

    const handleAddGrooming = () => {
        resetModalState();
        setShowModal(true);
    };

    const handleEditGrooming = (grooming) => {
        setEditingGrooming(grooming);
        setIsRecommended(!!(grooming.recomendation && grooming.recomendation.trim()));
        setGroomingDate(grooming.grooming_date || "");

        if (grooming.recomendation && grooming.recomendation.trim()) {
            setRecomendation(grooming.recomendation || "");
            setSelectedGroomings([]);
            setSelectedGroomingRows([]);
        } else {
            const siblingRows = petGroomings.filter(g =>
                String(g.appointment_id) === String(grooming.appointment_id) && !g.recomendation
            );

            setSelectedGroomings(siblingRows.map(g => g.grooming_id));
            setSelectedGroomingRows(siblingRows.map(g => ({
                rowKey: `existing-${g.id}`,
                id: g.id,
                grooming_id: g.grooming_id,
                grooming_name: g.grooming?.name || '',
                price: g.price !== null && g.price !== undefined ? g.price.toString() : "",
                reminder_date: g.reminder_date || '',
            })));
            setRecomendation("");
        }

        const noteHolder = grooming.recomendation
            ? grooming
            : petGroomings.find(g =>
                String(g.appointment_id) === String(grooming.appointment_id) &&
                !g.recomendation &&
                g.note && Array.isArray(g.note) && g.note.length > 0
            ) || grooming;

        if (noteHolder.note && Array.isArray(noteHolder.note) && noteHolder.note.length > 0) {
            setGeneralNote(noteHolder.note[0].note || "");
            setExistingNoteId(noteHolder.note[0].id);
        } else {
            setGeneralNote("");
            setExistingNoteId(null);
        }

        setShowModal(true);
    };

    const handleDeleteGrooming = async (ids) => {
        try {
            setSubmitting(true);
            const AuthToken = localStorage.getItem("authToken");
            const idsString = Array.isArray(ids) ? ids.join(',') : ids;
            const response = await axios.delete(
                `${process.env.REACT_APP_API_URL}/pet-grooming-delete/${idsString}`,
                { headers: { Authorization: `Bearer ${AuthToken}` } }
            );

            if (response.data) {
                fetchPetGroomings();
                showSuccessToast(response.data.message || t('Grooming deleted successfully!'));
            }
        } catch (error) {
            console.error("Error deleting grooming:", error);
            showErrorToast(t('failed to delete grooming'));
        } finally {
            setSubmitting(false);
        }
    };

    const handleDeleteClick = (groomingId, groomingName, isGroup = false, groupIds = []) => {
        const deleteIds = isGroup ? groupIds : [groomingId];
        const message = isGroup
            ? t("Are you sure you want to delete all grooming records for this appointment?")
            : t("You won't be able to revert this!");

        Swal.fire({
            title: t('Are you sure?'),
            text: message,
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#d33',
            cancelButtonColor: '#3085d6',
            confirmButtonText: t('Yes, delete it!'),
            cancelButtonText: t('Cancel'),
        }).then((result) => {
            if (result.isConfirmed) {
                handleDeleteGrooming(deleteIds);
            }
        });
    };

    const handleOpenComplete = (grooming) => {
        setSelectedCompleteGrooming(grooming);
        setCompletePrice(grooming.price !== null && grooming.price !== undefined ? grooming.price.toString() : "");
        setCompleteReminderDate(grooming.reminder_date || "");
        setCompleteStatus(grooming.reminder_status || "pending");
        setCompleteModal(true);
    };

    const resetCompleteModal = () => {
        setCompleteModal(false);
        setSelectedCompleteGrooming(null);
        setCompletePrice("");
        setCompleteReminderDate("");
        setCompleteStatus("pending");
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (selectedCompleteGrooming) {
            if (completeStatus === "completed") {
                const priceNum = completePrice === "" ? 0 : parseFloat(completePrice);
                if (priceNum <= 0) {
                    showWarningToast(t('please_enter_price'));
                    return;
                }
                if (!completeReminderDate) {
                    showWarningToast(t('please_select_reminder_date'));
                    return;
                }
            }
        }

        if (!selectedCompleteGrooming && !isRecommended) {
            if (!selectedGroomings || selectedGroomings.length === 0) {
                showWarningToast(t('please select grooming'));
                return;
            }
        }

        setSubmitting(true);

        try {
            const AuthToken = localStorage.getItem("authToken");
            let url = "";
            let payload = {};

            if (selectedCompleteGrooming) {
                url = `${process.env.REACT_APP_API_URL}/pet-grooming-update/${selectedCompleteGrooming.id}`;
                payload = {
                    appointment_id: appointmentId,
                    is_repeat: true,
                    grooming_id: selectedCompleteGrooming.grooming_id,
                    price: completePrice === "" ? 0 : parseFloat(completePrice) || 0,
                    reminder_date: completeReminderDate,
                    reminder_status: completeStatus,
                };
            } else {
                const anchorId = editingGrooming ? editingGrooming.id : null;

                url = editingGrooming
                    ? `${process.env.REACT_APP_API_URL}/pet-grooming-update/${anchorId}`
                    : `${process.env.REACT_APP_API_URL}/pet-grooming`;

                payload = {
                    appointment_id: appointmentId,
                    grooming_rows: !isRecommended ? selectedGroomingRows.map(row => ({
                        id: row.id || undefined,
                        grooming_id: row.grooming_id,
                        price: row.price === "" ? 0 : parseFloat(row.price) || 0,
                        reminder_date: row.reminder_date || null,
                    })) : undefined,
                    recomendation: isRecommended ? (recomendation || null) : null,
                    grooming_date: groomingDate || null,
                    is_repeat: false,
                };
            }

            if (generalNote && generalNote.trim()) {
                payload.note = generalNote.trim();
                if (existingNoteId) {
                    payload.note_id = existingNoteId;
                }
            } else if (existingNoteId) {
                payload.note = "";
                payload.note_id = existingNoteId;
            }

            const response = await axios({
                method: 'post',
                url: url,
                data: payload,
                headers: { Authorization: `Bearer ${AuthToken}` }
            });

            if (response.data) {
                setShowModal(false);
                if (selectedCompleteGrooming) {
                    resetCompleteModal();
                    showSuccessToast(t('Grooming completed successfully!'));
                } else {
                    showSuccessToast(editingGrooming ? t('Grooming updated successfully!') : t('Grooming added successfully!'));
                }
                fetchPetGroomings();
            }
        } catch (error) {
            console.error("Error saving grooming:", error);
            showErrorToast(error.response?.data?.message || t('failed_to_save_grooming'));
        } finally {
            setSubmitting(false);
        }
    };

    const getGroomingOptions = () => {
        const options = [];
        groomings.forEach(parent => {
            options.push({
                value: null,
                label: parent.name,
                isDisabled: true,
                isParent: true
            });

            if (parent.child_groomings && parent.child_groomings.length > 0) {
                parent.child_groomings.forEach(child => {
                    options.push({
                        value: child.id,
                        label: `   └ ${child.name}`,
                        price: child.price
                    });
                });
            }
        });
        return options;
    };

    const customSelectStyles = {
        option: (provided, state) => ({
            ...provided,
            backgroundColor: state.data.isDisabled ? '#f0f0f0' : state.isFocused ? '#e0e0e0' : 'white',
            color: state.data.isDisabled ? '#999' : '#333',
            fontStyle: state.data.isDisabled ? 'italic' : 'normal',
            fontWeight: state.data.isDisabled ? 'bold' : 'normal',
            cursor: state.data.isDisabled ? 'not-allowed' : 'pointer',
            padding: '10px',
        }),
        control: (provided) => ({
            ...provided,
            borderColor: '#ced4da',
            '&:hover': {
                borderColor: '#86b7fe'
            }
        })
    };

    const groupGroomingsByAppointment = () => {
        const grouped = {};
        petGroomings.forEach(grooming => {
            const groomingAppointmentId = grooming.appointment_id;
            if (!grouped[groomingAppointmentId]) {
                grouped[groomingAppointmentId] = {
                    appointment: grooming.appointment,
                    groomings: []
                };
            }
            grouped[groomingAppointmentId].groomings.push(grooming);
        });
        return grouped;
    };

    if (loading) {
        return (
            <div className="text-center py-5">
                <Spinner animation="border" variant="primary" />
                <p className="mt-3 mb-0">{t('loading groomings')}</p>
            </div>
        );
    }

    const groomingOptions = getGroomingOptions();
    const groupedGroomings = groupGroomingsByAppointment();

    return (
        <div>
            <div className="d-flex justify-content-between align-items-center mb-4 pb-2 border-bottom">
                <h6 className="mb-0 text-muted">{t('groomings list')}</h6>
                {isAppointmentOpen && !hasGroomingForAppointment &&
                    hasPermission("Appointment Grooming Create") && (
                        <Button
                            variant="primary"
                            size="sm"
                            onClick={handleAddGrooming}
                            className="d-flex align-items-center gap-2"
                        >
                            <FaPlus size={12} /> {t('add grooming')}
                        </Button>
                    )}
            </div>

            {!petGroomings || petGroomings.length === 0 ? (
                <Alert variant="info" className="text-center py-4">
                    <FaCut size={30} className="mb-2 text-muted" />
                    <p className="mb-0">{t('no groomings found')}</p>
                </Alert>
            ) : (
                <Accordion
                    activeKey={openAccordion}
                    onSelect={(key) => setOpenAccordion(key)}
                    className="appointments-accordion"
                >
                    {Object.entries(groupedGroomings).map(([appointmentIdKey, appointmentData]) => {
                        const { appointment, groomings: appointmentGroomings } = appointmentData;
                        const groupIds = appointmentGroomings.map(g => g.id);
                        const hasRecomendation = appointmentGroomings.some(g => g.recomendation);
                        const firstGrooming = appointmentGroomings[0];
                        const noteText = firstGrooming?.note && Array.isArray(firstGrooming.note) && firstGrooming.note.length > 0
                            ? firstGrooming.note[0].note
                            : null;

                        // Get all grooming names as a comma-separated string
                        const groomingNames = appointmentGroomings
                            .filter(g => !g.recomendation && g.grooming?.name)
                            .map(g => g.grooming.name)
                            .join(', ');

                        return (
                            <Accordion.Item eventKey={appointmentIdKey} key={appointmentIdKey} className="mb-3 border rounded">
                                <Accordion.Header className="bg-light">
                                    <div className="d-flex justify-content-between align-items-center w-100 me-3 pe-3">
                                        <div className="d-flex flex-column flex-md-row align-items-md-center gap-2 gap-md-3">
                                            <div className="text-muted small">
                                                <strong className="text-primary">{appointment?.appointment_number || 'Unknown Appointment'}</strong>
                                            </div>
                                            <Badge bg="info" className="px-3 py-2">
                                                {appointmentGroomings.length} {t('grooming(s)')}
                                            </Badge>
                                            {hasRecomendation && (
                                                <Badge bg="warning" className="px-3 py-2">
                                                    {t('Has Recommendations')}
                                                </Badge>
                                            )}
                                        </div>
                                    </div>
                                </Accordion.Header>
                                <Accordion.Body className="bg-white p-3">
                                    <div className="card border rounded">
                                        <div className="card-header bg-light py-3">
                                            <div className="d-flex justify-content-between align-items-center flex-wrap gap-2">
                                                <div className="d-flex flex-wrap align-items-center gap-2">
                                                    <FaStethoscope className="text-primary" />
                                                    <div className="text-muted small">
                                                        👨‍⚕️ <strong>Doctor:</strong> {firstGrooming?.doctor?.name || 'Unknown Doctor'}
                                                    </div>
                                                    {firstGrooming?.grooming_date && (
                                                        <div className="d-flex align-items-center gap-3 text-muted small">
                                                            <span className="d-flex align-items-center gap-1">
                                                                <MdDateRange size={14} />
                                                                {moment(firstGrooming.grooming_date).format("DD MMM YYYY")}
                                                            </span>
                                                            <span className="d-flex align-items-center gap-1">
                                                                <MdAccessTime size={14} />
                                                                {moment(firstGrooming.grooming_date).format("hh:mm A")}
                                                            </span>
                                                        </div>
                                                    )}
                                                </div>
                                                <div className="d-flex gap-2">
                                                    {!hasRecomendation && isAppointmentOpen &&
                                                        appointmentGroomings.some(g =>
                                                            String(g.appointment_id) !== String(appointmentId) &&
                                                            g.reminder_date &&
                                                            (!g.reminder_status || g.reminder_status === 'pending' || g.reminder_status === 'processing' || g.reminder_status === null)
                                                        ) &&
                                                        hasPermission("Appointment Grooming edit") && (
                                                            <Button
                                                                variant="outline-success"
                                                                size="sm"
                                                                onClick={() => handleOpenComplete(firstGrooming)}
                                                                disabled={firstGrooming?.reminder_status === 'completed'}
                                                                className="d-flex align-items-center gap-1"
                                                                style={{
                                                                    background: firstGrooming?.reminder_status === 'completed' ? '#e2e8f0' : '#38a169',
                                                                    color: firstGrooming?.reminder_status === 'completed' ? '#718096' : '#fff',
                                                                    borderColor: firstGrooming?.reminder_status === 'completed' ? '#cbd5e0' : '#38a169'
                                                                }}
                                                            >
                                                                <FaCheckCircle size={12} />
                                                                <span className="d-none d-sm-inline">{t('complete')}</span>
                                                            </Button>
                                                        )}
                                                    {hasPermission("Appointment Grooming edit") && (
                                                        <Button
                                                            variant="outline-primary"
                                                            size="sm"
                                                            onClick={() => handleEditGrooming(firstGrooming)}
                                                            className="d-flex align-items-center gap-1"
                                                            style={{ background: '#3182ce', color: '#fff' }}
                                                        >
                                                            <FaEdit size={12} />
                                                            <span className="d-none d-sm-inline">{t('edit')}</span>
                                                        </Button>
                                                    )}
                                                    {canDelete() && (
                                                        <Button
                                                            variant="danger"
                                                            size="sm"
                                                            onClick={() => handleDeleteClick(firstGrooming.id, firstGrooming.grooming?.name)}
                                                            className="d-flex align-items-center gap-1"
                                                        >
                                                            <FaTrash size={12} />
                                                            <span className="d-none d-sm-inline">{t('delete')}</span>
                                                        </Button>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                        <div className="card-body">
                                            <Row className="g-4">
                                                <Col md={12}>
                                                    <div className="p-3 bg-light rounded">
                                                        {hasRecomendation ? (
                                                            appointmentGroomings.map((grooming, index) => {
                                                                if (!grooming) return null;
                                                                return (
                                                                    <div key={grooming.id} className={index > 0 ? 'mt-3 pt-3 border-top' : ''}>
                                                                        {grooming.recomendation && (
                                                                            <div className="mb-2">
                                                                                <strong className="text-muted">{t('Recommendation')}:</strong> {grooming.recomendation}
                                                                            </div>
                                                                        )}
                                                                        {grooming.reminder_date && (
                                                                            <div className="mb-2">
                                                                                <strong className="text-muted">{t('Reminder Date')}:</strong>{' '}
                                                                                {moment(grooming.reminder_date).format("DD MMM YYYY hh:mm A")}
                                                                            </div>
                                                                        )}
                                                                        {grooming.reminder_status && (
                                                                            <div className="mb-2">
                                                                                <strong className="text-muted">{t('Reminder Status')}:</strong>{' '}
                                                                                <Badge bg={getReminderStatusBadge(grooming.reminder_status)}>
                                                                                    {grooming.reminder_status}
                                                                                </Badge>
                                                                            </div>
                                                                        )}
                                                                    </div>
                                                                );
                                                            })
                                                        ) : (
                                                            <>
                                                                <div className="mb-2">
                                                                    <strong className="text-muted">{t('Grooming Name')}:</strong> {groomingNames || 'N/A'}
                                                                </div>
                                                                {appointmentGroomings.some(g => g.grooming?.description) && (
                                                                    <div className="mb-2">
                                                                        <strong className="text-muted">{t('Description')}:</strong>{' '}
                                                                        {appointmentGroomings.map(g => g.grooming?.description).filter(Boolean).join(', ')}
                                                                    </div>
                                                                )}
                                                                {appointmentGroomings.some(g => g.reminder_date) && (
                                                                    <div className="mb-2">
                                                                        <strong className="text-muted">{t('Reminder Date')}:</strong>{' '}
                                                                        {appointmentGroomings.map(g => g.reminder_date ? moment(g.reminder_date).format("DD MMM YYYY hh:mm A") : null).filter(Boolean).join(', ')}
                                                                    </div>
                                                                )}
                                                                {appointmentGroomings.some(g => g.reminder_status) && (
                                                                    <div className="mb-2">
                                                                        <strong className="text-muted">{t('Reminder Status')}:</strong>{' '}
                                                                        {appointmentGroomings.filter(g => g.reminder_status).map(g =>
                                                                            <Badge key={g.id} bg={getReminderStatusBadge(g.reminder_status)} className="me-1">
                                                                                {g.reminder_status}
                                                                            </Badge>
                                                                        )}
                                                                    </div>
                                                                )}
                                                            </>
                                                        )}

                                                        {noteText && (
                                                            <div className="mt-3 pt-2 border-top">
                                                                <div className="d-flex align-items-center gap-2">
                                                                    <FaFileAlt className="text-success" />
                                                                    <strong className="text-muted">{t('note')}:</strong>
                                                                </div>
                                                                <p className="mb-0 mt-1 text-secondary">{noteText}</p>
                                                            </div>
                                                        )}
                                                    </div>
                                                </Col>
                                            </Row>
                                        </div>
                                    </div>
                                </Accordion.Body>
                            </Accordion.Item>
                        );
                    })}
                </Accordion>
            )}

            <Modal show={showModal} onHide={() => {
                setShowModal(false);
                resetModalState();
            }} size="xl" centered>
                <Modal.Header closeButton className="p-3">
                    <Modal.Title>
                        {editingGrooming ? t('Edit Grooming') : t('Add Grooming')}
                    </Modal.Title>
                </Modal.Header>
                <Form onSubmit={handleSubmit}>
                    <Modal.Body>
                        <Form.Group className="mb-3">
                            <Form.Check
                                type="checkbox"
                                id="recommended-checkbox"
                                label={<span className="fw-bold">{t('Recommended')}</span>}
                                checked={isRecommended}
                                onChange={(e) => setIsRecommended(e.target.checked)}
                            />
                        </Form.Group>

                        {!isRecommended ? (
                            <>
                                <Form.Group className="mb-3">
                                    <Form.Label className="fw-bold">
                                        {t('Select Groomings')} <span className="text-danger">*</span>
                                    </Form.Label>
                                    <Select
                                        options={groomingOptions}
                                        value={groomingOptions.filter(opt => selectedGroomings.includes(opt.value))}
                                        onChange={handleGroomingSelect}
                                        placeholder={t('select grooming')}
                                        isSearchable
                                        isMulti
                                        styles={customSelectStyles}
                                        formatOptionLabel={(option) => (
                                            <span style={{
                                                marginLeft: option.isParent ? '0' : '20px',
                                                fontWeight: option.isParent ? 'bold' : 'normal',
                                                color: option.isParent ? '#666' : '#333'
                                            }}>
                                                {option.label}
                                            </span>
                                        )}
                                    />
                                    <Form.Text className="text-muted">
                                        {t('You can select multiple groomings to add, keep, or remove at once.')}
                                    </Form.Text>
                                </Form.Group>

                                {selectedGroomingRows.length > 0 && (
                                    <div className="mb-4">
                                        <div className="d-flex justify-content-between align-items-center mb-2">
                                            <strong>{t('Selected Groomings')}</strong>
                                            <small className="text-muted">{t('Edit price and reminder date before saving')}</small>
                                        </div>

                                        <div className="table-responsive">
                                            <table className="table table-bordered table-striped text-nowrap mb-0">
                                                <thead className="table-light">
                                                    <tr>
                                                        <th>{t('Grooming Name')}</th>
                                                        <th>{t('Price')}</th>
                                                        <th>{t('Reminder Date')}</th>
                                                        <th>{t('Action')}</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {selectedGroomingRows.map((row) => (
                                                        <tr key={row.rowKey}>
                                                            <td className="align-middle fw-semibold">{row.grooming_name || t('Grooming')}</td>
                                                            <td>
                                                                <Form.Control
                                                                    size="sm"
                                                                    type="number"
                                                                    step="0.01"
                                                                    value={row.price ?? ''}
                                                                    onFocus={(e) => e.target.select()}
                                                                    onChange={(e) => handleGroomingRowChange(row.rowKey, 'price', e.target.value)}
                                                                    placeholder={t('Price')}
                                                                />
                                                            </td>
                                                            <td>
                                                                <Form.Control
                                                                    size="sm"
                                                                    type="datetime-local"
                                                                    value={row.reminder_date}
                                                                    onChange={(e) => handleGroomingRowChange(row.rowKey, 'reminder_date', e.target.value)}
                                                                />
                                                            </td>
                                                            <td className="text-center align-middle">
                                                                <Button
                                                                    variant="danger"
                                                                    size="sm"
                                                                    onClick={() => removeSelectedGroomingRow(row.rowKey)}
                                                                >
                                                                    {t('Remove')}
                                                                </Button>
                                                            </td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        </div>
                                    </div>
                                )}

                                <Form.Group className="mb-3">
                                    <Form.Label className="fw-bold d-flex align-items-center gap-2">
                                        <FaCalendarAlt /> {t('Grooming Date')}
                                    </Form.Label>
                                    <Form.Control
                                        type="datetime-local"
                                        value={groomingDate}
                                        onChange={(e) => setGroomingDate(e.target.value)}
                                    />
                                    <Form.Text className="text-muted">
                                        {t('Applies to the whole group.')}
                                    </Form.Text>
                                </Form.Group>
                            </>
                        ) : (
                            <>
                                <Form.Group className="mb-3">
                                    <Form.Label className="fw-bold">
                                        {t('Recommendations')}
                                    </Form.Label>
                                    <Form.Control
                                        as="textarea"
                                        rows={3}
                                        value={recomendation}
                                        onChange={(e) => setRecomendation(e.target.value)}
                                        placeholder={t('Enter recommendations here...')}
                                    />
                                </Form.Group>

                                <Form.Group className="mb-3">
                                    <Form.Label className="fw-bold d-flex align-items-center gap-2">
                                        <FaCalendarAlt /> {t('Grooming Date')}
                                    </Form.Label>
                                    <Form.Control
                                        type="datetime-local"
                                        value={groomingDate}
                                        onChange={(e) => setGroomingDate(e.target.value)}
                                    />
                                </Form.Group>
                            </>
                        )}

                        <Form.Group className="mb-3">
                            <Form.Label className="fw-bold d-flex align-items-center gap-2">
                                <FaFileAlt /> {t('Note')}
                            </Form.Label>
                            <Form.Control
                                as="textarea"
                                rows={3}
                                value={generalNote}
                                onChange={(e) => setGeneralNote(e.target.value)}
                                placeholder={t('Add any additional notes here...')}
                            />
                            <Form.Text className="text-muted">
                                {t('One note is kept for the whole group.')}
                            </Form.Text>
                        </Form.Group>
                    </Modal.Body>
                    <Modal.Footer className="p-3">
                        <Button variant="secondary" onClick={() => setShowModal(false)}>
                            {t('cancel')}
                        </Button>
                        <Button type="submit" variant="primary" disabled={submitting}>
                            {submitting ? (
                                <>
                                    <Spinner animation="border" size="sm" className="me-2" />
                                    {t('saving')}
                                </>
                            ) : (
                                editingGrooming ? t('update') : t('save')
                            )}
                        </Button>
                    </Modal.Footer>
                </Form>
            </Modal>

            <Modal show={completeModal} onHide={resetCompleteModal}>
                <Modal.Header closeButton className="p-2">
                    <Modal.Title>{t('Complete Grooming')}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Form.Group className="mb-3">
                        <Form.Label>{t('Status')}</Form.Label>
                        <Form.Select
                            value={completeStatus}
                            onChange={(e) => setCompleteStatus(e.target.value)}
                        >
                            <option value="pending">{t('Pending')}</option>
                            <option value="completed">{t('Completed')}</option>
                            <option value="missed">{t('Missed')}</option>
                            <option value="cancelled">{t('Cancelled')}</option>
                        </Form.Select>
                    </Form.Group>

                    <div className="mb-3">
                        <Badge bg={getReminderStatusBadge(completeStatus)} className="px-3 py-2">
                            {completeStatus === "completed" ? t('Completed') : completeStatus === "pending" ? t('Pending Completion') : completeStatus === "missed" ? t('Missed') : t('Cancelled')}
                        </Badge>
                    </div>

                    <Form.Group className="mb-3">
                        <Form.Label>{t('Grooming Name')}</Form.Label>
                        <Form.Control
                            type="text"
                            value={selectedCompleteGrooming?.grooming?.name || ""}
                            disabled
                        />
                    </Form.Group>

                    {completeStatus === "completed" && (
                        <>
                            <Form.Group className="mb-3">
                                <Form.Label>{t('Price')}</Form.Label>
                                <Form.Control
                                    type="number"
                                    step="0.01"
                                    value={completePrice ?? ''}
                                    onFocus={(e) => e.target.select()}
                                    onChange={(e) => setCompletePrice(e.target.value)}
                                    placeholder={t('Enter price')}
                                />
                            </Form.Group>

                            <Form.Group className="mb-3">
                                <Form.Label>{t('Next Reminder Date')}</Form.Label>
                                <Form.Control
                                    type="datetime-local"
                                    value={completeReminderDate}
                                    onChange={(e) => setCompleteReminderDate(e.target.value)}
                                />
                            </Form.Group>
                        </>
                    )}
                </Modal.Body>
                <Modal.Footer className="p-2">
                    <Button variant="secondary" onClick={resetCompleteModal}>
                        {t('cancel')}
                    </Button>
                    <Button variant="success" onClick={handleSubmit} disabled={submitting}>
                        {submitting ? <Spinner animation="border" size="sm" /> : t('Save & Complete')}
                    </Button>
                </Modal.Footer>
            </Modal>
        </div>
    );
}